import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Injector,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild
} from '@angular/core';
import { NG_VALUE_ACCESSOR, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { SavingFavoritesService } from '@shared/services/savingFavorites.service';
import { ControlValueAccessorDirective } from '@shared/base-classes/control-value-accessor.directive';
import { ALIGNED_LEFT_POSITIONS } from '@shared/components/object-selector/positions';
import { getOptionClassName, getOptionId, idNameCompareWith, CancelEvent, ICancelableControl } from '@shared/utils/selectors-utils';
import { FavoritesEnum } from '@shared/models/enums/favorites.enum';
import { DropDownItem } from '@shared/models/drop-down-item';
import { TranslateOnDemandPipe } from '../../pipes/translate-on-demand.pipe';
import { MapperPipe } from '../../pipes/mapper.pipe';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TlkButtonComponent } from '@itero/ui-toolkit-angular/button';
import { TlkSelectComponent, TlkSelectOptionComponent } from '@itero/ui-toolkit-angular/select';
import { RxStarCheckboxComponent } from '../rx-star-checkbox/rx-star-checkbox.component';
import { NgIf, NgFor, NgClass } from '@angular/common';

@Component({
	selector: 'rx-favorite-selector',
	templateUrl: './favorite-selector.component.html',
	styleUrls: ['./favorite-selector.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: FavoriteSelectorComponent,
			multi: true
		}
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		NgIf,
		NgFor,
		NgClass,
		FormsModule,
		ReactiveFormsModule,
		TlkSelectComponent,
		TlkSelectOptionComponent,
		TlkButtonComponent,
		RxStarCheckboxComponent,
		TranslateModule,
		MapperPipe,
		TranslateOnDemandPipe
	]
})
export class FavoriteSelectorComponent extends ControlValueAccessorDirective<DropDownItem> implements OnInit, OnChanges, ICancelableControl<DropDownItem> {
	@Input() id: string;
	@Input() label: string;
	@Input() secondaryLabel: string;
	@Input() labelSeparator: string;
	@Input() isRequired: boolean;
	@Input() isDisabled: boolean;
	@Input() isReadOnly: boolean;
	@Input() isCancellable: boolean;
	@Input() options: DropDownItem[] = [];
	@Input() compareWith: (o1: DropDownItem, o2: DropDownItem) => boolean = idNameCompareWith;

	@Input()
	favoriteName: FavoritesEnum;
	@Input()
	enableSimpleMode: boolean;

	@Input()
	minimumItemsCountToEnable = 7;
	selectedValues: Set<string>;
	@ViewChild('favSelect') favSelect: TlkSelectComponent<DropDownItem>;
	private temporarySelectedValues: Set<string> = new Set<string>();
	isInEditMode = false;
	enableFavoriteManagement = true;

	protected readonly position: ConnectedPosition[] = ALIGNED_LEFT_POSITIONS;

	generateOptionId = (option: DropDownItem): string => getOptionId(option?.Name, option?.Id, this.label ?? '');

	getOptionClassName = ({ index, option }: { index: number; option: DropDownItem }): Record<string, boolean> => ({
		...getOptionClassName(index),
		hidden: !!option?.Hidden
	});

	get isCancelButtonDisabled(): boolean {
		const value = this.control?.value;

		return this.isCancellable && (value === undefined || value === null);
	}

	@Output() cancelSelection = new EventEmitter<CancelEvent<DropDownItem>>();

	handleCancelDefault(event: Event): void {
		event.stopPropagation();
		this.control.patchValue(null as unknown as DropDownItem);
	}

	handleCancelSelection(event: Event): void {
		this.handleCancelDefault(event);
		this.cancelSelection.emit({ event, selector: this });
	}

	/**
	 * Resolves option names that carry the on-demand translation prefix. Reused for both the option list
	 * (where the template already applies the `translateOnDemand` pipe) and the closed trigger value, which
	 * `tlk-select` renders via {@link getOptionValue}.
	 */
	private readonly translateOnDemandPipe: TranslateOnDemandPipe;

	constructor(injector: Injector,
				private readonly savingFavoritesService: SavingFavoritesService,
				private readonly translateService: TranslateService,
				private readonly cdr: ChangeDetectorRef) {
		super(injector);
		this.translateOnDemandPipe = new TranslateOnDemandPipe(this.translateService, this.cdr);
	}

	/** Value shown in the closed trigger; translated when the option name carries the on-demand prefix. */
	getOptionValue = (option: DropDownItem): string => {
		return this.translateOnDemandPipe.transform(option?.Name ?? '');
	};

	ngOnInit(): void {
		if (!this.favoriteName) {
			this.enableSimpleMode = true;

			return;
		}
		if (this.enableSimpleMode) {
			return;
		}

		this.selectedValues = this.getSavedOptions();
		this.temporarySelectedValues = new Set(this.selectedValues);
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	ngOnChanges(changes: SimpleChanges) {
		this.enableFavoriteManagement = !this.enableSimpleMode && this.options?.length >= this.minimumItemsCountToEnable;
	}

	switchMode() {
		this.isInEditMode = !this.isInEditMode;
		this.cdr.markForCheck();
	}

	setViewMode(isWindowOpened: boolean) {
		if (!isWindowOpened) {
			this.isInEditMode = false;
			this.cdr.markForCheck();
		}
	}

	changeSelection(favorite: DropDownItem) {
		if (this.temporarySelectedValues.has(favorite.Id)) {
			this.temporarySelectedValues.delete(favorite.Id);
		} else {
			this.temporarySelectedValues.add(favorite.Id);
		}
		this.cdr.markForCheck();
	}

	isTheSame(index: number, item: DropDownItem) {
		return item.Id;
	}

	showNoFavorites(favoriteOptions: DropDownItem[]) {
		return !favoriteOptions?.length || favoriteOptions.every(o => o.Hidden);
	}

	get shouldShowNoFavorites(): boolean {
		const favOptions = this.getFavoriteOptions();
		return !favOptions?.length || favOptions.every(o => o.Hidden);
	}

	getFavoriteOptions(_trigger?: any) {
		return this.options?.filter(option => this.selectedValues?.has(option.Id)) ?? [];
	}

	getOptions(_trigger: any, viewOptions: boolean) {
		if (!this.enableFavoriteManagement) {
			return this.options;
		}
		if (viewOptions) {
			return this.isInEditMode ? null : this.options?.filter(option => !this.selectedValues?.has(option.Id));
		}

		return this.isInEditMode ? this.options?.filter(option => !this.selectedValues?.has(option.Id)) : null;
	}

	saveFavorites() {
		this.savingFavoritesService.save(this.favoriteName, Array.from(this.temporarySelectedValues));
		this.selectedValues = new Set(this.temporarySelectedValues);
		this.switchMode();
		this.scrollToTop();
	}

	getFavoritePanelClass() {
		return this.enableFavoriteManagement ? 'favorites favorites-offset' : 'favorites';
	}

	onPanelOpen() {
		this.cdr.markForCheck();
	}

	onCancelSelection(event: Event) {
		this.handleCancelSelection(event);
		this.favSelect?.close();
	}

	private getSavedOptions(): Set<string> {
		return new Set(this.savingFavoritesService.get(this.favoriteName));
	}

	private scrollToTop() {
		setTimeout(() => {
			this.favSelect?.panel?.nativeElement?.scrollTo({ top: 0, behavior: 'smooth' });
		}, 2);
	}
}
